import * as XLSX from "xlsx";

/**
 * Helper untuk export payroll data ke Excel
 * Mendukung multiple schema dengan jumlah payroll items yang berbeda-beda
 */

class PayrollExcelExporter {
  /**
   * Format currency untuk display
   */
  static formatCurrency(value, currency = "Rp") {
    const formatted = new Intl.NumberFormat("id-ID", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
    return `${currency} ${formatted}`;
  }

  /**
   * Collect semua unique payroll items dari semua employees
   * Ini penting untuk memastikan semua kolom ada meskipun schema berbeda
   */
  static collectAllPayrollItems(payrollData) {
    const allItems = new Map();

    payrollData.forEach((employee) => {
      employee.raw.result_detail.groups.forEach((group) => {
        group.items.forEach((item) => {
          const key = `${group.group.name}|${item.name}`;
          if (!allItems.has(key)) {
            allItems.set(key, {
              groupName: group.group.name,
              groupOperator: group.group.operator,
              itemName: item.name,
              itemType: item.type,
            });
          }
        });
      });
    });

    return Array.from(allItems.values());
  }

  /**
   * Get payroll item value untuk employee tertentu
   */
  static getPayrollItemValue(employee, groupName, itemName) {
    const group = employee.raw.result_detail.groups.find(
      (g) => g.group.name === groupName
    );

    if (!group) return 0;

    const item = group.items.find((i) => i.name === itemName);
    return item ? item.calculated_value : 0;
  }

  /**
   * Export Summary Sheet - Overview semua employee
   */
  static createSummarySheet(payrollData, payrollInfo) {
    const summaryData = payrollData.map((item, index) => ({
      No: index + 1,
      "User ID": item.user_id,
      Name: item.name,
      Email: item.email,
      "Base Salary": item.salary,
      Currency: item.currency,
      "Remaining Paid Leave": item.remaining_paid_leave,
      "Working Days": item.details.variables.working_days,
      "Attendance Days": item.details.variables.total_attendance_days,
      "Working Hours": item.workingHours,
      "Regular Overtime Hours":
        item.details.variables.total_regular_overtime_hours,
      "Holiday Overtime Hours":
        item.details.variables.total_holiday_overtime_hours,
      "Late Minutes": item.details.variables.late_minutes,
      "Total Leave": item.total_leave,
      "Hourly Rate": item.details.variables.salary_hourly_rate,
      "Daily Rate": item.details.variables.salary_daily_rate,
      "Gross Salary": item.details.gross_salary,
      "Total Deductions": item.details.total_deductions,
      "Take Home Pay": item.takeHomePay,
      "Period From": item.raw.date_from,
      "Period To": item.raw.date_to,
      "Schema Template": item.raw.schema_used.settings.template_name,
    }));

    return XLSX.utils.json_to_sheet(summaryData);
  }

  /**
   * Export Detailed Sheet - Breakdown lengkap semua payroll items
   * Menghandle schema yang berbeda dengan membuat kolom untuk semua kemungkinan items
   */
  static createDetailedSheet(payrollData) {
    // Collect all unique payroll items
    const allItems = this.collectAllPayrollItems(payrollData);

    // Build header
    const detailedData = payrollData.map((item, index) => {
      const row = {
        No: index + 1,
        "User ID": item.user_id,
        Name: item.name,
        Email: item.email,
        Currency: item.currency,
        "Base Salary": item.salary,
        "Working Days": item.details.variables.working_days,
        "Attendance Days": item.details.variables.total_attendance_days,
        "Working Hours": item.workingHours,
      };

      // Add all payroll items as columns
      allItems.forEach(({ groupName, itemName, groupOperator }) => {
        const value = this.getPayrollItemValue(item, groupName, itemName);
        const columnName = `${groupName} - ${itemName}`;

        // Jika deduction (subtract), buat nilai negatif
        row[columnName] =
          groupOperator === "subtract" && value !== 0 ? -value : value;
      });

      // Add summary columns
      row["Gross Salary"] = item.details.gross_salary;
      row["Total Deductions"] = item.details.total_deductions;
      row["Take Home Pay"] = item.takeHomePay;

      return row;
    });

    return XLSX.utils.json_to_sheet(detailedData);
  }

  /**
   * Export Schema Information Sheet - Detail schema yang digunakan per employee
   */
  static createSchemaSheet(payrollData) {
    const schemaData = payrollData.map((item, index) => ({
      No: index + 1,
      Name: item.name,
      "Template Name": item.raw.schema_used.settings.template_name,
      "Attendance Type": item.raw.schema_used.settings.attendance_type,
      "Tolerance Minutes": item.raw.schema_used.settings.tolerance_minutes,
      "Break Time Hours": item.raw.schema_used.settings.break_time_hours,
      "Break Time Apply To": item.raw.schema_used.settings.break_time_apply_to,
      "Regular OT Schema":
        item.raw.schema_used.settings.overtime_regular_schema,
      "Holiday OT Schema":
        item.raw.schema_used.settings.overtime_holiday_schema,
      "Regular OT Multiplier":
        item.raw.schema_used.settings.regular_flat_multiplier ||
        "Fixed Sequence",
      "Holiday OT Multiplier":
        item.raw.schema_used.settings.holiday_flat_multiplier ||
        "Fixed Sequence",
    }));

    return XLSX.utils.json_to_sheet(schemaData);
  }

  /**
   * Export Overtime Rules Sheet - Detail aturan overtime
   */
  static createOvertimeRulesSheet(payrollData) {
    const overtimeData = [];

    payrollData.forEach((item, index) => {
      const schema = item.raw.schema_used.settings;

      // Regular Overtime Rules
      if (
        schema.overtime_regular_fixed_sequence &&
        schema.overtime_regular_fixed_sequence.length > 0
      ) {
        schema.overtime_regular_fixed_sequence.forEach((rule) => {
          overtimeData.push({
            Employee: item.name,
            Type: "Regular Day",
            Hour: rule.hour,
            Multiplier: rule.multiplier,
            Range: "-",
          });
        });
      }

      // Holiday Overtime Rules
      if (
        schema.overtime_holiday_fixed_sequence &&
        schema.overtime_holiday_fixed_sequence.length > 0
      ) {
        schema.overtime_holiday_fixed_sequence.forEach((rule) => {
          overtimeData.push({
            Employee: item.name,
            Type: "Holiday",
            Hour: rule.type === "single" ? rule.hour : "-",
            Multiplier: rule.multiplier,
            Range: rule.type === "range" ? `${rule.from}-${rule.to}` : "-",
          });
        });
      }
    });

    return XLSX.utils.json_to_sheet(overtimeData);
  }

  /**
   * Export Groups & Items Definition Sheet
   */
  static createGroupsItemsSheet(payrollData) {
    const groupsItems = [];

    payrollData.forEach((item) => {
      const schema = item.raw.schema_used;

      schema.payroll_items.forEach((payItem) => {
        const group = schema.payroll_groups.find(
          (g) => g.id === payItem.group_id
        );

        groupsItems.push({
          Employee: item.name,
          "Group Name": group ? group.name : "Unknown",
          "Group Operator": group ? group.operator : "Unknown",
          "Item Name": payItem.name,
          "Item Type": payItem.type,
          Formula: payItem.formula,
          "Is Taxable": payItem.is_taxable ? "Yes" : "No",
        });
      });
    });

    return XLSX.utils.json_to_sheet(groupsItems);
  }

  /**
   * Main export function
   */
  static exportToExcel(payrollData, payrollInfo = {}) {
    try {
      // Create workbook
      const wb = XLSX.utils.book_new();

      // Sheet 1: Summary (Overview)
      const summarySheet = this.createSummarySheet(payrollData, payrollInfo);
      XLSX.utils.book_append_sheet(wb, summarySheet, "Summary");

      // Sheet 2: Detailed Breakdown
      const detailedSheet = this.createDetailedSheet(payrollData);
      XLSX.utils.book_append_sheet(wb, detailedSheet, "Detailed Breakdown");

      // Sheet 3: Schema Information
      const schemaSheet = this.createSchemaSheet(payrollData);
      XLSX.utils.book_append_sheet(wb, schemaSheet, "Schema Info");

      // Sheet 4: Overtime Rules
      const overtimeSheet = this.createOvertimeRulesSheet(payrollData);
      XLSX.utils.book_append_sheet(wb, overtimeSheet, "Overtime Rules");

      // Sheet 5: Groups & Items Definition
      const groupsSheet = this.createGroupsItemsSheet(payrollData);
      XLSX.utils.book_append_sheet(wb, groupsSheet, "Groups & Items");

      // Generate filename
      const payrollName = payrollInfo.payroll_name || "Payroll";
      const dateStr = new Date().toISOString().split("T")[0];
      const filename = `${payrollName}_${dateStr}.xlsx`;

      // Apply column widths for better readability
      Object.keys(wb.Sheets).forEach((sheetName) => {
        const sheet = wb.Sheets[sheetName];
        const range = XLSX.utils.decode_range(sheet["!ref"]);
        const colWidths = [];

        for (let C = range.s.c; C <= range.e.c; ++C) {
          let maxWidth = 10;
          for (let R = range.s.r; R <= range.e.r; ++R) {
            const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
            const cell = sheet[cellAddress];
            if (cell && cell.v) {
              const cellLength = cell.v.toString().length;
              if (cellLength > maxWidth) {
                maxWidth = Math.min(cellLength, 50); // Max 50 chars width
              }
            }
          }
          colWidths.push({ wch: maxWidth + 2 });
        }

        sheet["!cols"] = colWidths;
      });

      // Download file
      XLSX.writeFile(wb, filename);

      return {
        success: true,
        filename: filename,
        message: "Excel file exported successfully",
      };
    } catch (error) {
      console.error("Export to Excel failed:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to export Excel file",
      };
    }
  }

  /**
   * Export hanya Summary Sheet (untuk quick export)
   */
  static exportSummaryOnly(payrollData, payrollInfo = {}) {
    try {
      const wb = XLSX.utils.book_new();
      const summarySheet = this.createSummarySheet(payrollData, payrollInfo);
      XLSX.utils.book_append_sheet(wb, summarySheet, "Payroll Summary");

      const payrollName = payrollInfo.payroll_name || "Payroll";
      const dateStr = new Date().toISOString().split("T")[0];
      const filename = `${payrollName}_Summary_${dateStr}.xlsx`;

      XLSX.writeFile(wb, filename);

      return {
        success: true,
        filename: filename,
        message: "Summary exported successfully",
      };
    } catch (error) {
      console.error("Export summary failed:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to export summary",
      };
    }
  }
}

export default PayrollExcelExporter;
